import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { authTenantServiceApi, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenantConfig } from "@/hooks/useTenantConfig";
import ReadOnlyNotice, { useIsAdmin } from "@/components/ReadOnlyNotice";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { COUNTRIES } from "@/lib/countries";
import { Loader2, Building2, Trash2 } from "lucide-react";

const SIZES = ["1-50", "51-200", "201-500", "501-1000", "1001-5000", "5000+"];
const INDUSTRIES = ["Technology", "IT Staffing", "Healthcare", "Finance", "Manufacturing", "Retail", "Education", "Other"];

/**
 * Company Profile — step 1 of tenant setup. The identity facts
 * (company name, domain, plan, status) come from GET /tenants/:id and
 * are read-only here (they are set at signup / changed via Billing). The
 * editable descriptive fields (industry, size, country, website,
 * description) persist into tenant_configurations.featureFlags.companyProfile
 * via the real PATCH /configuration — the same document every other
 * settings page writes.
 */
export default function AdminCompanyProfile() {
  const { user } = useAuth();
  const { config, loading: cfgLoading, save, error: cfgError, setError } = useTenantConfig();
  const [tenant, setTenant] = useState(null);
  const [profile, setProfile] = useState({ industry: "", companySize: "", country: "", website: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const admin = useIsAdmin();

  useEffect(() => {
    if (!user?.tenantId) return;
    authTenantServiceApi
      .get(`/api/v1/tenants/${user.tenantId}`)
      .then((res) => setTenant(res.data))
      .catch((err) => { const m = errorMessage(err); if (m) setError(m); });
  }, [user, setError]);

  useEffect(() => {
    const p = config?.featureFlags?.companyProfile;
    if (p) setProfile((prev) => ({ ...prev, ...p }));
  }, [config]);

  const set = (patch) => setProfile((prev) => ({ ...prev, ...patch }));

  const EMPTY_PROFILE = { industry: "", companySize: "", country: "", website: "", description: "" };

  const onSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const data = await save({ featureFlags: { ...(config?.featureFlags || {}), companyProfile: profile } });
      // Reflect exactly what the server persisted (authoritative), not the
      // transient typed state — removes any "looks unsaved" ambiguity and
      // resets fields the user cleared that the echo omits.
      setProfile({ ...EMPTY_PROFILE, ...(data?.featureFlags?.companyProfile || {}) });
      setSaved(true);
    } catch (err) {
      const m = errorMessage(err);
      if (m) setError(m);
    } finally {
      setSaving(false);
    }
  };

  const clearProfile = async () => {
    if (!window.confirm("Clear all company profile details? The organization facts (name, plan) are not affected.")) return;
    setSaving(true);
    setSaved(false);
    try {
      const data = await save({ featureFlags: { ...(config?.featureFlags || {}), companyProfile: EMPTY_PROFILE } });
      setProfile({ ...EMPTY_PROFILE, ...(data?.featureFlags?.companyProfile || {}) });
    } catch (err) {
      const m = errorMessage(err);
      if (m) setError(m);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout variant="tenant">
      <h1 className="font-heading text-2xl font-bold">Company Profile</h1>
      <p className="mt-1 text-muted-foreground">Basic information about your organization.</p>
      <ReadOnlyNotice what="the company profile" />
      {cfgError && <p className="mt-6 text-sm text-destructive" data-testid="company-error">{cfgError}</p>}
      {cfgLoading && <Loader2 className="mt-8 h-6 w-6 animate-spin text-primary" />}

      {!cfgLoading && (
        <div className="mt-6 max-w-3xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><Building2 className="h-4 w-4" /> Organization</CardTitle>
              <CardDescription>Set at signup. Change your plan in Billing.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2" data-testid="company-facts">
              <Fact label="Company name" value={tenant?.companyName} />
              <Fact label="Primary domain" value={tenant?.domain} />
              <Fact label="Plan" value={tenant?.planCode} />
              <Fact label="Status" value={tenant?.status} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Industry</Label>
                <Select value={profile.industry} onValueChange={(v) => set({ industry: v })} disabled={!admin}>
                  <SelectTrigger className="mt-1.5" data-testid="company-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Company size</Label>
                <Select value={profile.companySize} onValueChange={(v) => set({ companySize: v })} disabled={!admin}>
                  <SelectTrigger className="mt-1.5" data-testid="company-size"><SelectValue placeholder="Select size" /></SelectTrigger>
                  <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s} employees</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Combobox
                  id="country"
                  className="mt-1.5"
                  data-testid="company-country"
                  aria-label="Country"
                  disabled={!admin}
                  options={COUNTRIES}
                  value={profile.country}
                  onChange={(v) => set({ country: v })}
                  placeholder="Select country"
                  searchPlaceholder="Search countries…"
                  emptyText="No matching country."
                />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input id="website" className="mt-1.5" data-testid="company-website" disabled={!admin} placeholder="https://example.com" value={profile.website} onChange={(e) => set({ website: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" className="mt-1.5" data-testid="company-description" disabled={!admin} value={profile.description} onChange={(e) => set({ description: e.target.value })} />
              </div>
            </CardContent>
          </Card>

          {admin && (
            <div className="flex items-center gap-3">
              <Button onClick={onSave} disabled={saving} data-testid="company-save-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save &amp; continue
              </Button>
              <Button variant="outline" onClick={clearProfile} disabled={saving} data-testid="company-delete-btn" className="gap-1.5 text-destructive hover:text-destructive">
                <Trash2 className="h-4 w-4" /> Clear details
              </Button>
              {saved && <p className="text-sm text-emerald-600" data-testid="company-saved">Saved.</p>}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

function Fact({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-medium capitalize">{value || <span className="italic text-muted-foreground">—</span>}</p>
    </div>
  );
}
